import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  ElementRef,
  ErrorHandler,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EditorSaveStatus } from '../../editor-save-status';
import { EditorDownloads } from '../../editor-downloads';
import { EditorSaves } from '../../editor-saves';
import { SheetVersionHistoryDialog } from '../../services/sheet-version-history-dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SHEETS_UI_ENGINE_PANELS,
  type SheetsUiCommand,
  type SheetsUiInspector,
  type SheetsUiItemTab,
  type SheetsUiSheetRef,
  type SheetsUiSurface,
} from '@sharedlib/common/sheets/ui-commands';
import { SignalStitchEventType, SignalType } from '@sharedlib/common/types/signal-type';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { SignalsService } from '@frontendlib/common/services/signals.service';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { firstValueFrom, of, type Subscription } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { SheetsAppStateService } from '@modules/sheets/services/state.service';
import { SheetsEditorNavigationService } from '@modules/sheets/services/sheets-editor-navigation.service';
import { Sheet } from '@modules/sheets/types/sheet';
import { Application } from '@modules/common/types/application';
import { ModalService } from '@modules/modal/services/modal.service';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { EditorChrome } from '../../editor-chrome';
import { openWorkbookInEditor } from '../../open-workbook';
import { runUiCommand, type UiHost } from '../../ui-commands';
import { type UiInspector } from '@sharedlib/sheets-editor/ui-inspector';
import { menuOptions, toolbarControl, type UiOption } from '../../ui-registry';
import { isEnginePanel } from '@sharedlib/sheets-editor/engine-panels';
import { UI_REASONS } from '../../ui-reasons';
import { IconComponent } from '../../icon.component';
import { ImportDialogComponent } from '../../import-dialog.component';
import { SplitPanelComponent } from '../../split-panel.component';
import { ValidationPanelComponent } from '../../validation-panel.component';
import { CfPanelComponent } from '../../cf-panel.component';
import { DownloadMenuComponent, type DownloadAction } from '../../download-menu.component';
import { PdfDialogComponent, type PdfSheetChoice } from '../../pdf-dialog.component';
import { ShortcutsDialogComponent } from '../../shortcuts-dialog.component';
import { planCsvSheet } from '@sharedlib/sheets-editor/csv-sheet';
import { buildEmptyWorkbook, buildSpikeWorkbook, SNAPSHOT_STYLES } from '@sharedlib/sheets-editor/fixture';
import { delimitedBytes } from '@sharedlib/sheets-editor/import-limits';
import { CalcTimeoutError, recalculateBeforeExport } from '@sharedlib/sheets-editor/recalculate-before-export';
import type { ActiveFormat, SheetsEditorHandle } from '@sharedlib/sheets-editor/engine-bootstrap';
import { OTHER_TAB_TOAST, SaveMachine, type SaveMachineState } from '../../save-machine';
import { loadAssetFrom, putSnapshot, uploadImageTo } from '../../save-http';
import { EditorSessionClient, type SessionState, sessionDirty } from '@sharedlib/sheets-editor/editor-session';
import { closeSession, postNavAck, sendSessionCall } from '../../session-http';
import { LOAD_ERROR_TOAST, exportFailedToast, preparingToast, requestFailedToast, uploadFailedToast } from '../../editor-toasts';
import { copyText } from '../../copy-text';
import { downloadBlob } from '../../download-blob';
import { shortcutText } from '../../platform-keys';
import { assertExhaustive } from '@modules/common/utils/switch';
import { toolbarFit, toolbarTarget } from '../../toolbar-keys';
import { isSaveShortcut } from '../../save-shortcut';
import type { CfRuleDraft, CfRuleView } from '@sharedlib/sheets-editor/cf-rules';
import type { ExistingValidation, ValidationRuleDraft } from '@sharedlib/sheets-editor/validation-rules';
import type { PdfOptions } from '@sharedlib/sheets-editor/workbook-pdf';
import {
  BORDER_STYLES,
  FONT_FAMILIES,
  NUMBER_FORMATS,
  ONE_COLUMN_REQUIRED,
  PALETTE,
  TOOLBAR,
  type MenuEntry,
  type ToolbarControl,
  type ToolbarRun,
  type ToolbarState,
  type ToolbarValue,
} from '@sharedlib/sheets-editor/toolbar-spec';
import { SHEETS_STRINGS, fill } from '@sharedlib/sheets-editor/sheets.strings';
import { importAndOpenWorkbook, type LibraryView, openInEditorFullWindow } from '../../new-workbook';
import { injectFullWindowStart } from '@modules/common/services/full-window.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { downloadName, workbookName } from '@sharedlib/sheets-editor/export-names';
import { DialogFocusDirective } from '../../dialog-focus.directive';
import { delimitedRows, type RowsSheet } from '@sharedlib/sheets-editor/sheet-rows';
import {
  REVIEW_MS,
  REVIEW_STROKE,
  REVIEW_STROKE_WIDTH,
  STITCHY_TOAST_MS,
  getRevisionChange,
  stitchyToastText,
  type ChangedRange,
} from '../../stitchy-change';
import { TopProgressService } from '@modules/top-progress/top-progress.service';

/** SM-SAVE's states, plus this editor's own for a grid bound to no item (the spike route, a failed load). */
type SaveState = SaveMachineState | 'unbound';

/**
 * The header text for each state, §5.4's exact wording. A `Record` rather than a switch with a
 * default: a `default` that returned 'Saved' is how `unbound` came to claim a save that never
 * happened, and a new state would inherit the same lie. This way it is a compile error instead.
 * `conflict_user`, `error_blocked` and `read_only` also show the dialog or a banner.
 */
const SAVE_LABELS = {
  clean: SHEETS_STRINGS.Saved,
  dirty: SHEETS_STRINGS['Unsaved changes'],
  saving: SHEETS_STRINGS['Saving…'],
  saving_dirty: SHEETS_STRINGS['Saving…'],
  retry_wait: SHEETS_STRINGS["Can't reach the server. Retrying…"],
  conflict_user: SHEETS_STRINGS['Unsaved changes'],
  error_blocked: SHEETS_STRINGS['Not saved'],
  read_only: SHEETS_STRINGS['In Trash'],
  // Not "Unsaved changes": that implies a save is coming. Nothing here is written anywhere.
  unbound: SHEETS_STRINGS['Not saved'],
} as const satisfies Record<SaveState, string>;

/** How long a toast shows; a link to copy by hand, longer. */
const TOAST_MS = 2_600;
const LINK_TOAST_MS = 10_000;

/** The toolbar's state before the grid first reports: the engine's defaults for an empty cell. */
const NO_FORMAT: ActiveFormat = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  fontFamily: 'Arial',
  fontSize: 11,
  hAlign: null,
  vAlign: null,
  wrap: 'overflow',
  numberFormat: '',
  merged: false,
  filter: false,
  filterConditions: false,
  frozen: false,
  painter: false,
  findOpen: false,
  gridlines: true,
  canUndo: false,
  canRedo: false,
};

@Component({
  selector: 'app-sheets-editor',
  // Standalone so the real app's PagesModule can use it directly. As a module
  // declaration it was reachable only through SheetsModule's own route, which is
  // what kept Sheets outside the app shell.
  standalone: true,
  imports: [
    ValidationPanelComponent,
    SplitPanelComponent,
    CfPanelComponent,
    IconComponent,
    DownloadMenuComponent,
    PdfDialogComponent,
    ShortcutsDialogComponent,
    ImportDialogComponent,
    DialogFocusDirective,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './sheets-editor.component.html',
  styleUrls: ['./sheets-editor.component.less', './sheets-editor.toast.less'],
  // SBUX-1043: marks an editor's own subtree, so a document-level keystroke can be told whether it
  // came from THIS editor, another one, or neither (`ownsSaveShortcut`). An attribute rather than the
  // element's name: the name is the selector, and nothing guarantees a host keeps it.
  host: { 'data-sheets-editor': '' },
})
export class SheetsEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('host', { static: true }) private readonly hostRef!: ElementRef<HTMLDivElement>;

  protected readonly loading = signal(true);
  /**
   * Set when `mount()` fails. `loading` alone cannot express it: the template's only states were
   * "loading" and "mounted", so a failed mount left "Loading the spreadsheet…" on screen for ever
   * with nothing else to show. See B-60.
   */
  protected readonly mountFailed = signal(false);
  protected readonly saveState = signal<SaveState>('clean');
  /** The `error_blocked` banner's reason (§5.4). */
  protected readonly blockedReason = signal<string | null>(null);
  /** The other-tab conflict dialog (§5.6). */
  protected readonly conflictOpen = signal(false);
  /** NFR-8: Appendix F's strings, from their one definition. */
  protected readonly S = SHEETS_STRINGS;
  protected readonly fill = fill;
  protected readonly toast = signal<string | null>(null);
  /** The revision the toast's "View versions" opens version history at (SBUX-1146); null when it has no action. */
  protected readonly toastVersions = signal<number | null>(null);
  /** AM-38: the toast for Stitchy's change, with Undo and Review (§5.5 step 4). */
  protected readonly stitchyToast = signal<{
    text: string;
    revision: number;
    ranges: readonly ChangedRange[];
    previous: unknown;
  } | null>(null);
  protected readonly menuOpen = signal(false);
  protected readonly pdfOpen = signal(false);
  /** Rows 64 and 68: the PDF dialog's sheets, the active one, and whether a PDF is being made. */
  protected readonly pdfSheets = signal<readonly PdfSheetChoice[]>([]);
  protected readonly pdfActiveSheet = signal<string | null>(null);
  protected readonly pdfBusy = signal(false);
  protected readonly importOpen = signal(false);
  protected readonly shortcutsOpen = signal(false);
  /** Shows which document is open; an imported file renames the header. */
  protected readonly docTitle = signal<string>(SHEETS_STRINGS.Untitled);
  /** True once the workbook resolved to a real item, i.e. the section shell is around us. */
  protected readonly boundToItem = signal(false);

  /**
   * SBUX-1043: the workbook this editor opens, when its HOST names it rather than the route.
   *
   * The Sheets page's editor is the route's: it reads `?id=`, tells the section what is open, and holds
   * the route guard. The fourth panel mounts a SECOND editor beside it, on a workbook of its own, and
   * must do none of those three things — each of them has one slot, and a second editor taking it would
   * change the main app's selection, rename the wrong workbook, or quietly lift the route's guard off
   * the editor that still needs it. `hosted()` is that distinction, in one place.
   */
  public readonly workbookId = input<string | null>(null);

  /** The workbook to open: the host's, or the route's. */
  private requestedId(): string | null {
    return this.workbookId() ?? this.route.snapshot.queryParamMap.get('id');
  }

  /** Whether the host named the workbook. A hosted editor drives nothing outside itself. */
  private hosted(): boolean {
    return !!this.workbookId();
  }

  /**
   * SBUX-1043: "Close workbook", asked of a HOSTED editor. The page's editor closes by leaving its
   * route; the panel's has no route to leave, and navigating would send the main app to `/sheets`
   * while the panel went on showing the workbook it was told to close. The host closes it instead,
   * through the same path as its own Back — guard and all.
   */
  public readonly closeRequested = output<void>();

  /**
   * SBUX-1043: the worksheet a HOST wants opened, in place of the route's `?sheet=` — which names a
   * tab in the ROUTE's workbook and so is ignored by a hosted editor. Read once, at mount, like the
   * workbook itself.
   */
  public readonly sheetId = input<string | null>(null);

  /**
   * The worksheet now showing, whenever it changes. The fourth panel remembers it, because the DP
   * destroys the panel on every app switch and the handoff counts the panel's worksheet among the
   * state that must survive one. Emitted from the engine's own context change, so it covers the
   * sheet bar, the sheet list and `activate_sheet` alike.
   */
  public readonly activeSheetChanged = output<string>();

  /** The last id emitted, so a selection moving inside one sheet says nothing. */
  private emittedSheetId: string | null = null;

  /**
   * SBUX-1043: the cell a HOST wants selected once its worksheet is open — the rest of the handoff's
   * "Panel worksheet, selected range and engine scroll". Applied after `sheetId`, so it lands on the
   * right sheet.
   */
  public readonly cell = input<string | null>(null);

  /**
   * Where the user is now, whenever it changes. Emitted rather than read on demand: by the time a
   * host's `ngOnDestroy` runs, this editor's handle is already disposed and the answer is null —
   * measured in the app, where the panel stored `null` on every app switch while the same call a
   * moment earlier returned "C5". A host keeps the last value it heard and writes that instead.
   */
  public readonly activeCellChanged = output<string>();

  /** The last cell emitted, so the engine's other context changes say nothing. */
  private emittedCell: string | null = null;

  private publishActiveCell(): void {
    const cell = this.handle?.context()?.activeCell ?? null;
    if (!cell || cell === this.emittedCell) return;
    this.emittedCell = cell;
    this.activeCellChanged.emit(cell);
  }

  private publishActiveSheet(): void {
    const id = this.handle?.context()?.activeSheet.sheetId ?? null;
    if (!id || id === this.emittedSheetId) return;
    this.emittedSheetId = id;
    this.activeSheetChanged.emit(id);
  }
  /** AM-78: bound to a workbook, the save state shows in the expanded header, not a row of its own. */
  private readonly saveStatus = inject(EditorSaveStatus);
  /** The app's error reporting (Sentry's handler in the app module). */
  private readonly errorHandler = inject(ErrorHandler, { optional: true });
  private readonly topProgress = inject(TopProgressService);
  private readonly publishSaveStatus = effect(() =>
    this.saveStatus.current.set(
      this.boundToItem() ? { state: this.saveState(), label: SAVE_LABELS[this.saveState()] } : null,
    ),
  );
  /** What the toolbar shows (§11.6.3's "Active when"), from the grid. */
  protected readonly format = signal<ActiveFormat>(NO_FORMAT);
  /** §11.6.3, as data: separators are `null`. */
  protected readonly toolbar: readonly (ToolbarControl | null)[] = TOOLBAR.map((item) =>
    'separator' in item ? null : item,
  );
  protected readonly palette = PALETTE;
  protected readonly borderStyles = BORDER_STYLES;
  /** The open menu's control id. */
  protected readonly openMenu = signal<string | null>(null);
  /**
   * How many of the toolbar's items fit in the row; the rest are in "More tools", in order. The
   * row is one row (§11.6.3), and a narrow window (or the Knowledge Panel open) left the last
   * controls behind a scrollbar (B-113).
   */
  protected readonly shownCount = signal(TOOLBAR.length);
  protected readonly overflowOpen = signal(false);
  protected readonly overflowAt = signal({ right: 0, top: 0 });
  @ViewChild('bar') private readonly barRef?: ElementRef<HTMLElement>;
  /** Each item's width, measured on every fit (an item out of the row is still laid out). */
  private readonly slotWidths: number[] = [];
  private barObserver?: ResizeObserver;
  /** #11 and #12's main parts apply the last-used colour; the bar under the icon shows it. */
  protected readonly textColour = signal('#E40000');
  protected readonly fillColour = signal('#FFFA66');
  /** #13's line style and colour, for the next border. */
  protected readonly borderStyle = signal('THIN');
  protected readonly borderColour = signal('#243B57');
  /** #27's first seven open the panel with that rule type. */
  protected readonly cfPreset = signal<string | null>(null);
  /** The open panel's context: the selection, the sheet's CF rules, the selection's validation. */
  protected readonly panelRange = signal('A1');
  /** The sheet a panel was opened on: its rule is applied to the active sheet, so it closes when that changes. */
  private panelSheetId: string | null = null;
  protected readonly cfRules = signal<readonly CfRuleView[]>([]);
  protected readonly validationExisting = signal<ExistingValidation | null>(null);
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private stitchyToastTimer: ReturnType<typeof setTimeout> | null = null;
  protected readonly validationOpen = signal(false);
  /** #31's split panel (B-177). */
  protected readonly splitOpen = signal(false);
  protected readonly cfOpen = signal(false);

  private handle: SheetsEditorHandle | null = null;

  /** This editor's own element: which of two editors a document-level keystroke belongs to. */
  private readonly element = inject(ElementRef<HTMLElement>);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly editorNavigation = inject(SheetsEditorNavigationService);
  private unregisterNavigation?: () => void;
  /** Optional: the unguarded spike route has no session, so it has no service either. */
  private readonly sheetsService = inject(SheetsService, { optional: true });
  /**
   * Optional for the same reason as the service above: the unguarded /sheets-spike route renders this
   * editor outside `*appSheetsAppContext`, where no app state exists.
   */
  private readonly sheetsState = inject(SheetsAppStateService, { optional: true });
  private readonly http = inject(HttpClient);
  private readonly viewContainer = inject(ViewContainerRef);
  private engineStyles?: ComponentRef<unknown>;
  private readonly stitchEvents = inject(StitchEventsService, { optional: true });
  private readonly spaceContext = inject(SpaceContextStore, { optional: true });
  /** The workbook being edited. Null on the spike route, where the fixture stands in. */
  private sheet: Sheet | null = null;
  /** SM-SAVE, once a workbook is bound; null while unbound. */
  private machine: SaveMachine | null = null;
  /** This tab's `X-Editor-Session-Id` (§5.2). One per open editor. */
  private readonly editorSessionId = crypto.randomUUID();
  private remoteEvents: Subscription | null = null;
  private navCommands: Subscription | null = null;
  /** The open workbook's title, followed from the app state (AM-48). */
  private titleChanges: Subscription | null = null;
  /** "Download and share", picked in the expanded header (the prototype's editor title bar). */
  private readonly downloads = inject(EditorDownloads);
  private headerPicks: Subscription | null = null;
  /** "Duplicate" and "Save as template" save this editor's edits first (`EditorSaves`); set while bound. */
  private readonly editorSaves = inject(EditorSaves);
  private unregisterSaves: (() => void) | null = null;
  private readonly signals = inject(SignalsService, { optional: true });
  /** This tab's editor session (§6, AM-46), once a workbook is bound. Same id as the saves'. */
  private session: EditorSessionClient | null = null;
  /** The last local edit, which the session reports for ALG-GUARD's two-second window. */
  private lastMutationAt: number | null = null;
  private destroyed = false;
  /** AM-46: the close goes as a beacon when the page itself goes away. */
  private readonly onPageHide = (): void => this.session?.close(true);
  /** Back from the back-forward cache: the session `pagehide` closed opens again. */
  private readonly onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) this.session?.resume();
  };
  /** Cmd/Ctrl+S saves now, as leaving the tab does, rather than opening the browser's "Save page as…". */
  private readonly onSaveShortcut = (event: KeyboardEvent): void => {
    if (!isSaveShortcut(event) || !this.ownsSaveShortcut(event.target)) return;
    event.preventDefault();
    void this.commitCellEdit().then(() => this.machine?.flush());
  };

  /**
   * SBUX-1043: ⌘S belongs to the editor the keystroke happened in.
   *
   * This listens on `document`, in capture, which was unambiguous while there was one editor. With
   * the fourth panel's beside the page's, one ⌘S reached both — each flushed its own save machine,
   * and each committed whatever cell was half-typed in it, in a workbook the user was not looking at.
   *
   * So: ours when the keystroke came from inside us. From inside the OTHER editor (which carries the
   * same `data-sheets-editor`), not ours. From neither — focus on the body, or in the app's chrome —
   * the page's editor takes it, because that is the editor the page is about; a panel one would be
   * saving something off to the side.
   */
  private ownsSaveShortcut(target: EventTarget | null): boolean {
    const node = target instanceof Node ? target : null;
    if (node && this.element.nativeElement.contains(node)) return true;
    const element = node instanceof Element ? node : (node?.parentElement ?? null);
    if (element?.closest('[data-sheets-editor]')) return false;
    return !this.hosted();
  }
  private readonly onVisibility = (): void => {
    // B-193: a cell being edited is kept first, then saved.
    if (document.visibilityState === 'hidden') void this.commitCellEdit().then(() => this.machine?.flush());
  };
  /** B-193: text typed in a cell and not yet entered is kept when the person leaves. */
  private async commitCellEdit(): Promise<boolean> {
    try {
      await this.zone.runOutsideAngular(() => this.handle?.commitCellEdit() ?? Promise.resolve());
      return true;
    } catch (error) {
      // The save still goes (with what the grid has); the console says why the typed text didn't.
      console.error('[sheets] B-193: the cell being edited could not be kept', error);
      return false;
    }
  }

  /**
   * "Save now, and say whether everything is saved", for a copy of this workbook (`EditorSaves`): the cell being edited
   * is kept, the edits go now rather than after the debounce, and the answer waits for the server. False when they
   * couldn't be saved (the save banner says why) or the cell's text couldn't be kept: the copy would leave them out.
   */
  private async saveNow(machine: SaveMachine): Promise<boolean> {
    if (!(await this.commitCellEdit())) return false;
    machine.flush();
    await machine.settled();
    return !machine.hasUnsavedWork;
  }

  /**
   * Whether this editor can be left: the cell being typed in is kept first, and only work that
   * cannot be saved at all asks the person.
   *
   * SBUX-1043 made it public. On the Sheets page the route guard asks it; the fourth panel has no
   * route to leave, so its Back and its Close ask it directly — otherwise a half-typed cell went
   * when the workbook was closed, which the page's editor has never allowed.
   */
  public async canLeave(): Promise<boolean> {
    if (!(await this.commitCellEdit())) {
      this.showToast(SHEETS_STRINGS['The cell edit could not be kept. Stay and try again.']);
      return false;
    }
    // Leaving saves the rest (ngOnDestroy → leave).
    if (!this.machine?.hasUnsavableWork) return true;
    return this.editorNavigation.confirmUnsaved();
  }
  private readonly onBeforeUnload = (event: BeforeUnloadEvent): void => {
    // B-193: a cell being edited is unsaved work too. Asking keeps the page; the edit is kept
    // meanwhile, so staying saves it.
    const editing = this.handle?.isCellEditing() ?? false;
    if (editing) void this.commitCellEdit();
    if (this.machine?.hasUnsavedWork || editing) event.preventDefault();
  };

  public constructor(private readonly zone: NgZone) {}

  public ngOnInit(): void {
    // The route's guard belongs to the route's editor: `register` keeps ONE entry, so a hosted editor
    // registering would take the guard off the page's and leave its unsaved grid unprotected. The panel
    // asks before it closes a workbook itself.
    if (!this.hosted()) {
      this.unregisterNavigation = this.editorNavigation.register(this.requestedId() ?? '', () => this.canLeave());
    }
    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('pointerdown', this.closeMenuOutside, true);
    document.addEventListener('keydown', this.onSaveShortcut, true);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
    void this.mount();
  }

  public ngOnDestroy(): void {
    this.unregisterNavigation?.();
    this.barObserver?.disconnect();
    // Leaving the editor saves now (§5.4, "Leaving the page"): the document is taken before the engine
    // goes, and the save, its retries and the save after one in flight outlive the component.
    if (this.machine)
      this.machine.leave(
        this.machine.hasUnsavedWork ? this.zone.runOutsideAngular(() => this.handle?.save()) : undefined,
      );
    this.unregisterSaves?.();
    this.unregisterSaves = null;
    // The save `leave` started outlives the editor: a copy made meanwhile waits for it, until it ends.
    const machine = this.machine;
    if (this.sheet && machine?.pending) {
      const off = this.editorSaves.register(this.sheet.id, async () => {
        await machine.settled();
        return !machine.hasUnsavedWork;
      });
      void machine.settled().then(off);
    }
    // After the flush, so the last report is not "unsaved" for a save already on its way.
    this.session?.close(false);
    this.destroyed = true;
    this.remoteEvents?.unsubscribe();
    this.navCommands?.unsubscribe();
    this.titleChanges?.unsubscribe();
    this.headerPicks?.unsubscribe();
    if (this.stitchyToastTimer) clearTimeout(this.stitchyToastTimer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('pointerdown', this.closeMenuOutside, true);
    document.removeEventListener('keydown', this.onSaveShortcut, true);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
    // An error from the engine's dispose must not stop the teardown: thrown here, it stopped the router's navigation
    // halfway, the grid gone and the route unchanged, a blank page (the Custom format bug, fixed at its cause in
    // ac403e638). It is logged and reported, and the rest of the teardown and the navigation go on.
    try {
      this.zone.runOutsideAngular(() => this.handle?.dispose());
    } catch (error) {
      console.error('[sheets] the engine could not be disposed; the editor closes anyway', error);
      this.errorHandler?.handleError(error);
    }
    this.handle = null;
    this.publishSaveStatus.destroy();
    this.saveStatus.current.set(null);
    this.saveStatus.sheets.set(null);
  }

  /** §5.6: "Keep my changes". */
  protected keepMine(): void {
    this.conflictOpen.set(false);
    this.machine?.userKeepMine();
  }

  /** §5.6: "Load the other version". */
  protected loadOther(): void {
    this.conflictOpen.set(false);
    void this.machine?.userReload();
  }

  /** The `error_blocked` banner's "Try again". */
  protected retrySave(): void {
    this.machine?.userRetry();
  }

  /** The `read_only` banner's "Restore": out of Trash, then editable again (transition 20). */
  protected restoreFromTrash(): void {
    if (!this.sheet || !this.sheetsService) return;
    this.sheetsService
      .delete({ ids: [this.sheet.id] }, false)
      .pipe(take(1))
      .subscribe({
        next: () => void this.restored(),
        error: () => this.zone.run(() => this.showToast(fill('Could not restore this workbook'))),
      });
  }

  /**
   * ITEM_RESTORED (transition 20): from the banner's "Restore", or the restore event when it was restored
   * elsewhere (§12 #6). The app state let go of the workbook when it went to Trash (BaseAppStateService
   * clears the main view), so it is given back, and the header shows the title again.
   */
  private async restored(): Promise<void> {
    const machine = this.machine;
    if (!machine) return;
    try {
      await machine.itemRestored();
    } catch (error) {
      // The stored workbook could not be read back: the grid stays read-only, and the user is told.
      console.error('[sheets-editor] reloading the restored workbook failed', error);
      this.zone.run(() => this.showToast(fill('Could not restore this workbook')));
      return;
    }
    const sheet = this.sheet;
    if (sheet && machine.state === 'clean' && !this.destroyed && !this.hosted()) this.zone.run(() => this.sheetsState?.setMainView(sheet));
  }

  protected saveLabel(): string {
    return SAVE_LABELS[this.saveState()];
  }

  /**
   * A panel applies to the active sheet when it is saved (and the split panel to the live selection), while
   * it shows what it was opened on: moved to another sheet (by a click, or Stitchy's navigation), the
   * validation and formatting panels put their rule on that sheet's same range instead, or edited a rule
   * that isn't there; the split panel split whatever column was selected. So a panel closes when what it
   * was opened on is no longer what is current.
   */
  private closeMovedPanels(): void {
    if (!this.validationOpen() && !this.cfOpen() && !this.splitOpen()) return;
    const context = this.handle?.context();
    if (!context) return;
    const movedSheet = this.panelSheetId !== null && context.activeSheet.sheetId !== this.panelSheetId;
    const movedSelection = this.splitOpen() && this.selectionA1() !== this.panelRange();
    if (!movedSheet && !movedSelection) return;
    this.zone.run(() => {
      this.validationOpen.set(false);
      this.cfOpen.set(false);
      this.splitOpen.set(false);
    });
  }

  /** Where a panel opens: the sheet it applies to, and the selection, in A1, where its rule goes. */
  private openPanelHere(): string {
    this.panelSheetId = this.handle?.context()?.activeSheet.sheetId ?? null;
    return this.selectionA1();
  }

  /** The selection, in A1, where a panel's new rule goes. */
  private selectionA1(): string {
    const context = this.handle?.context();
    return context?.selections?.[0] ?? context?.activeCell ?? 'A1';
  }

  /** Only one panel occupies the slot at a time, as frames 04 and 05 show. */
  protected toggleValidation(): void {
    this.cfOpen.set(false);
    this.splitOpen.set(false);
    if (!this.validationOpen()) {
      const range = this.openPanelHere();
      this.panelRange.set(range);
      // The selection's own rule, if it has one, opens to be edited.
      this.validationExisting.set(this.handle?.validationAt(range) ?? null);
    }
    this.validationOpen.set(!this.validationOpen());
  }

  protected toggleCf(): void {
    this.validationOpen.set(false);
    this.splitOpen.set(false);
    if (!this.cfOpen()) this.prepareCf();
    this.cfOpen.set(!this.cfOpen());
  }

  private prepareCf(): void {
    this.panelRange.set(this.openPanelHere());
    this.refreshCfRules();
  }

  /** The active sheet's rules, for the open CF panel; after every change to the grid. */
  private refreshCfRules(): void {
    try {
      this.cfRules.set(this.handle?.cfRules() ?? []);
    } catch (err) {
      console.warn('[sheets] reading conditional formatting failed', err);
    }
  }

  /** #31's "Split text to columns…": the panel asks for the separator, then runs the command with it. */
  private openSplit(): void {
    this.validationOpen.set(false);
    this.cfOpen.set(false);
    this.panelRange.set(this.openPanelHere());
    this.splitOpen.set(true);
  }

  protected applySplit(delimiter: string): void {
    const handle = this.handle;
    if (!handle) return;
    try {
      void handle.runToolbar('sheet.command.split-text-to-columns', delimiter, {
        style: this.borderStyle(),
        color: this.borderColour(),
      });
      this.splitOpen.set(false);
    } catch (err) {
      // ONE_COLUMN_REQUIRED's words, as Stitchy is told them.
      this.showToast(
        err instanceof Error && err.message === ONE_COLUMN_REQUIRED ? ONE_COLUMN_REQUIRED : fill('Select a cell first'),
      );
    }
  }

  protected applyValidation(draft: ValidationRuleDraft): void {
    try {
      this.handle?.applyValidation(draft);
      this.validationOpen.set(false);
    } catch (err) {
      console.error('[sheets] applying validation failed', err);
      this.showToast(err instanceof Error ? err.message : SHEETS_STRINGS['The rule could not be saved']);
    }
  }

  protected removeValidation(id: string): void {
    this.handle?.removeValidation(id);
    this.validationOpen.set(false);
  }

  protected applyCf(draft: CfRuleDraft): void {
    try {
      this.handle?.applyCfRule(draft);
      this.cfOpen.set(false);
    } catch (err) {
      console.error('[sheets] applying conditional formatting failed', err);
      this.showToast(err instanceof Error ? err.message : SHEETS_STRINGS['The rule could not be saved']);
    }
  }

  protected removeCf(id: string): void {
    this.handle?.removeCfRule(id);
    this.refreshCfRules();
  }

  private async mount(): Promise<void> {
    try {
      await this.mountEditor();
    } catch (err) {
      // `ngOnInit` calls this as `void this.mount()`, so nothing catches a rejection and the
      // component simply stops before `loading.set(false)` -- leaving "Loading the spreadsheet…"
      // on screen permanently. The first thing awaited below is a dynamic `import()`, which is
      // precisely what fails for a tab left open across a deploy, so this is a routine failure
      // rather than a theoretical one.
      console.error('[sheets-editor] the editor failed to mount', err);
      this.zone.run(() => {
        this.mountFailed.set(true);
        this.loading.set(false);
        this.showToast(fill('The spreadsheet editor failed to load — reload the page'));
      });
    }
  }

  private async mountEditor(): Promise<void> {
    const [{ createSheetsEditor }, { EngineStylesComponent }] = await this.topProgress.track(
      Promise.all([import('@sharedlib/sheets-editor/engine-bootstrap'), import('../../engine-styles.component')]),
    );
    // Closed while it loaded (the engine's chunk takes a while the first time): nothing more is set up,
    // or a session, an engine and a preview change outlived the editor that asked for them.
    if (this.destroyed) return;
    // B-139: the engine's stylesheets and the skin, created here rather than in the template so they
    // ride in the lazy chunk with the engine (NFR-1). A component's styles are in the page from its
    // creation, before the engine measures anything, and leave with the editor.
    this.engineStyles ??= this.viewContainer.createComponent(EngineStylesComponent);
    // The engine measures its container once at construction. In ngOnInit the element is
    // in the DOM but not yet laid out, so it reads height 0 and paints a zero-height
    // canvas that never recovers.
    //
    // Do NOT gate this on requestAnimationFrame: rAF does not fire in a tab that is
    // not compositing (a background tab, or a hidden pane), so the editor would never
    // mount at all. That matters beyond this component — AM-19's pooled engine pages
    // are backgrounded by definition. setTimeout still fires there.
    await this.whenLaidOut(this.hostRef.nativeElement);
    if (this.destroyed) return;
    // The engine's render loop re-arms requestAnimationFrame every frame and zone.js patches
    // rAF, so building it inside the zone would run change detection at the display
    // refresh rate and ApplicationRef.isStable would never fire.
    // The workbook named by ?id=, or the fixture when there is none -- which is
    // the unguarded /sheets-spike route, where there is no session to load one with.
    const snapshot = await this.loadSnapshot();
    if (this.destroyed) return;

    const handle = await this.zone.runOutsideAngular(() =>
      createSheetsEditor(this.hostRef.nativeElement, snapshot, {
        onMutation: () => this.onLocalMutation(),
        onFormat: (f) =>
          this.zone.run(() => {
            this.format.set(f);
            if (this.cfOpen()) this.refreshCfRules();
            // What the toolbar shows (a size, a format's name) can change an item's width.
            queueMicrotask(() => this.fitToolbar());
          }),
        // AM-43: the rename was already refused by then; this only tells the user why.
        onSheetNameRejected: (message) => this.zone.run(() => this.showToast(message)),
        // §8.4: an inserted image is uploaded at once and the drawing points at the asset (T-IMG-1).
        uploadImage: this.sheet
          ? this.uploadWithToast(uploadImageTo(this.http, this.sheet.id, this.workbookTenant()))
          : undefined,
        loadAsset: this.sheet ? loadAssetFrom(this.http, this.workbookTenant()) : undefined,
        onContextChange: () => {
          this.closeMovedPanels();
          this.reportSession();
          this.publishSheetCount();
          this.publishActiveSheet();
          this.publishActiveCell();
        },
      }),
    );
    if (this.destroyed) {
      this.zone.runOutsideAngular(() => handle.dispose());
      return;
    }
    this.handle = handle;
    this.publishSheetCount();
    this.openRequestedSheet();
    // After the requested sheet, so the host hears the sheet it asked for rather than the first one.
    this.publishActiveSheet();
    this.publishActiveCell();
    this.followTitle();
    this.bindSaving();
    // Bound, the menu is the header's: what is picked there runs here.
    if (this.boundToItem()) this.headerPicks = this.downloads.picked.subscribe((action) => this.zone.run(() => this.onDownload(action)));
    this.zone.run(() => {
      this.loading.set(false);
      // A PDF dialog opened while the engine loaded gets its sheets now.
      if (this.pdfOpen()) this.refreshPdfSheets();
    });
  }

  /** The PDF dialog's "Sheet" list: every sheet, in tab order, and the active one (row 64). */
  private refreshPdfSheets(): void {
    const workbook = this.handle?.engine.getActiveWorkbook();
    this.pdfSheets.set((workbook?.getSheets() ?? []).map((sheet) => ({ id: sheet.getSheetId(), name: sheet.getSheetName() })));
    this.pdfActiveSheet.set(workbook?.getActiveSheet()?.getSheetId() ?? null);
  }

  /**
   * AM-48: the title is the workbook's name. Renamed in the header (or anywhere that changes the open item in
   * the app state), the engine's workbook takes the new name, which the downloads and the PDF are named from.
   */
  private followTitle(): void {
    // A hosted editor's workbook is not the main view's, so the main view's title says nothing about
    // it — following it would rename the panel's workbook after whatever the section has open.
    if (!this.sheet || !this.sheetsState || this.hosted()) return;
    // Compared with the title the app state last had, not the editor's item: a reload (a remote revision, a
    // restore) brings a fresher item than the one the app state may still hold, and its older title would
    // otherwise rename the workbook back.
    let seen = this.sheet.title;
    this.titleChanges = this.sheetsState.getMainView().subscribe((item) => {
      const sheet = this.sheet;
      if (!sheet || !item || item.id !== sheet.id || item.title === seen) return;
      seen = item.title;
      sheet.title = item.title;
      this.zone.runOutsideAngular(() => this.handle?.setWorkbookName(item.title));
    });
  }

  /**
   * The cell the host asked for, once its worksheet is active (SBUX-1043). A host's only: the route
   * names no cell. A range that no longer fits the sheet is simply not selected — `runNavCommand`
   * answers with an error, and there is nothing to tell the user, who asked for none of this.
   */
  private selectRequestedCell(sheetId: string): void {
    const range = this.hosted() ? this.cell() : null;
    if (!range) return;
    this.zone.runOutsideAngular(() => this.handle?.navigate({ select: { sheet: { id: sheetId }, range } }));
  }

  /** §11.5.2: a row of the preview's Sheets list opens the workbook on that sheet, named in the URL. */
  private openRequestedSheet(): void {
    // The route's `?sheet=` names a tab in the ROUTE's workbook; a hosted editor on another workbook
    // would open whichever of its own sheets happened to share that id.
    // A host names it directly; the route's `?sheet=` is the route editor's and nobody else's.
    const sheetId = this.hosted() ? this.sheetId() : this.route.snapshot.queryParamMap.get('sheet');
    if (!sheetId) return;
    const workbook = this.handle?.engine.getActiveWorkbook() as unknown as {
      getSheetBySheetId(id: string): unknown;
      setActiveSheet(sheet: unknown): unknown;
    } | null;
    const sheet = workbook?.getSheetBySheetId(sheetId);
    if (sheet) workbook!.setActiveSheet(sheet);
    this.selectRequestedCell(sheetId);
  }

  /** Starts SM-SAVE for the bound workbook, from the revision it was loaded at. */
  private bindSaving(): void {
    const sheet = this.sheet;
    if (!sheet || !this.sheetsService || !this.handle) return;
    // Fixed when saving starts: SM-SAVE's retries and the save after a close outlive the editor.
    const tenantId = this.workbookTenant();
    const machine = new SaveMachine(
      {
        now: () => Date.now(),
        snapshot: () => this.handle?.save(),
        put: (snapshot, base, saveId) =>
          putSnapshot(this.http, {
            workbookId: sheet.id,
            snapshot,
            base,
            saveId,
            editorSessionId: this.editorSessionId,
            tenantId,
          }),
        newSaveId: () => crypto.randomUUID(),
        // Timers outlive the editor: a save's retry does (`leave`); the machine stops the editor's own.
        setTimer: (ms, fire) => setTimeout(() => this.zone.run(fire), ms),
        clearTimer: (timer) => clearTimeout(timer as ReturnType<typeof setTimeout>),
        reload: (apply) => this.reloadFromServer(apply),
        setEditable: (editable) => this.zone.runOutsideAngular(() => this.handle?.setEditable(editable)),
        openConflictDialog: () => this.zone.run(() => this.conflictOpen.set(true)),
        // With "View versions" it stays as long as Stitchy's own toast with actions does: 2.6 s is too short to reach it.
        toast: (text, viewRevision) =>
          this.zone.run(() => this.showToast(text, viewRevision === undefined ? TOAST_MS : STITCHY_TOAST_MS, viewRevision)),
        stitchyApplied: (revision, previous) => void this.showStitchyChange(sheet.id, revision, previous),
        changed: () =>
          this.zone.run(() => {
            if (this.destroyed || this.machine !== machine) return;
            this.saveState.set(machine.state);
            this.blockedReason.set(machine.blockedReason);
            this.reportSession();
          }),
        illegal: (message) => console.warn(message),
        leftUnsaved: (reason) =>
          console.error(
            `sheets-save: workbook ${sheet.id}'s last edits were not saved after the editor closed (${reason})`,
          ),
      },
      sheet.revision,
      // AC-LIFE-2: a workbook opened from Trash opens read-only, with its banner.
      { trashed: sheet.deleted === true },
    );
    this.machine = machine;
    this.saveState.set(machine.state);
    this.unregisterSaves = this.editorSaves.register(sheet.id, () => this.saveNow(machine));
    this.bindSession(sheet.id);

    // REMOTE_REVISION: another tab's save, or Stitchy's, reaches this one as the UPDATED signal
    // the outbox publishes with the new revision (§5.7).
    const spaceId = this.spaceContext?.contextId();
    if (spaceId && this.stitchEvents) {
      // The Trash too, while it is open (item 10 of the editor brief; §12, SM-HOST): moved to Trash elsewhere
      // (another tab, Stitchy, the header's item menu), it turns read-only now with its banner, rather than
      // taking edits §12 refuses; restored elsewhere, it is editable again (§12 #6).
      this.remoteEvents = this.stitchEvents
        .getEvents(spaceId, StitchType.SHEET, [
          SignalStitchEventType.UPDATED,
          SignalStitchEventType.MOVED_TO_TRASH,
          SignalStitchEventType.RESTORED_FROM_TRASH,
        ])
        .pipe(filter((event) => event.ids.includes(sheet.id)))
        .subscribe((event) => {
          if (event.eventType === SignalStitchEventType.MOVED_TO_TRASH) return this.zone.run(() => machine.itemTrashed());
          if (event.eventType === SignalStitchEventType.RESTORED_FROM_TRASH) {
            if (machine.state === 'read_only') void this.restored();
            return;
          }
          const changes = event.changes as { revision?: number; lastActor?: 'user' | 'stitchy' };
          if (typeof changes.revision === 'number')
            void machine.remoteRevision(changes.lastActor ?? 'user', changes.revision);
        });
    }
  }

  /**
   * The editor session (§6) over HTTP (AM-46). Its calls need no change detection, so it runs
   * outside Angular, as the engine does.
   */
  private bindSession(workbookId: string): void {
    const shared = this.spaceContext?.type() === SpaceType.SHARED_SPACE ? this.spaceContext.contextId() : null;
    const tenantId = this.workbookTenant();
    this.session = new EditorSessionClient(
      this.editorSessionId,
      {
        now: () => Date.now(),
        send: (call) => sendSessionCall(this.http, workbookId, this.editorSessionId, call, tenantId),
        close: (beacon) => closeSession(this.http, this.editorSessionId, { beacon, sharedSpaceId: shared, tenantId }),
        setTimer: (ms, fire) => this.zone.runOutsideAngular(() => setTimeout(fire, ms)),
        clearTimer: (timer) => clearTimeout(timer as ReturnType<typeof setTimeout>),
      },
      this.sessionState()!,
    );
    this.zone.runOutsideAngular(() => this.session?.start());

    // ALG-NAV (§6.4): Stitchy moving this tab's view. The command names one editor session; this
    // tab runs only its own, and acknowledges each over HTTP (AM-46).
    this.navCommands =
      this.signals
        ?.getSignal(SignalType.SHEETS_NAV_COMMAND)
        .pipe(filter((signal) => signal.editorSessionId === this.editorSessionId && signal.workbookId === workbookId))
        .subscribe((signal) => {
          // The MCP contract's UI tools (6 Oct) come the same way, as `{ ui: command }`; the ack carries what shows.
          const ui = (signal.command as { ui?: SheetsUiCommand }).ui;
          if (ui) {
            void this.zone
              .run(() => runUiCommand(this.uiHost, ui))
              .then((outcome) =>
                postNavAck(
                  this.http,
                  this.editorSessionId,
                  'error' in outcome
                    ? { commandId: signal.commandId, ok: false, error: outcome.error.slice(0, 500) }
                    : { commandId: signal.commandId, ok: true, error: null, state: outcome.state },
                  tenantId,
                ),
              );
            return;
          }
          const outcome = this.zone.runOutsideAngular(() => this.handle?.navigate(signal.command as never)) ?? {
            ok: false,
            error: 'the editor is not ready',
          };
          postNavAck(
            this.http,
            this.editorSessionId,
            { commandId: signal.commandId, ok: outcome.ok, error: 'error' in outcome ? outcome.error : null },
            tenantId,
          );
        }) ?? null;
  }

  /**
   * The workbook's own tenant, named on every request the editor makes for it (`OWNING_SPACE_ID`): the
   * item's `accountId`, else the space it was opened in. Requests that outlive the editor (a save's
   * retry, the save on leaving, a heartbeat or close) then reach that space, not whichever one is current
   * when they go.
   */
  private workbookTenant(): string | null {
    return this.sheet?.accountId ?? this.spaceContext?.contextId() ?? null;
  }

  private sessionState(): SessionState | null {
    const machine = this.machine;
    if (!machine) return null;
    return {
      context: this.handle?.context() ?? null,
      // §7.5 (and B-194: a cell being edited counts), see sessionDirty.
      dirty: sessionDirty(machine.hasUnsavedWork, this.handle?.isCellEditing() ?? false),
      lastMutationAt: this.lastMutationAt,
      baseRevision: machine.baseRevision,
    };
  }

  private reportSession(): void {
    const state = this.sessionState();
    if (state && this.session) this.zone.runOutsideAngular(() => this.session?.report(state));
  }

  /**
   * GET the item and put its document in the engine (ALG-SHEETS-REPLACE); resolves to its revision. With
   * `apply` (a remote revision), only if it still holds once the item has arrived and no cell is being
   * typed in: otherwise the grid is kept and this resolves to null.
   */
  private async reloadFromServer(apply?: () => boolean): Promise<number | null> {
    const sheet = await new Promise<Sheet>((resolve, reject) =>
      this.sheetsService!.getItem(this.sheet!.id).pipe(take(1)).subscribe({ next: resolve, error: reject }),
    );
    if (this.destroyed || (apply && (!apply() || this.handle?.isCellEditing()))) return null;
    this.sheet = sheet;
    const stored = sheet.snapshot as { sheetOrder?: unknown[] } | undefined;
    // Named after the title, as on load (AM-48).
    const name = workbookName(sheet.title);
    this.zone.runOutsideAngular(() =>
      this.handle?.replace(stored?.sheetOrder?.length ? { ...stored, name } : buildEmptyWorkbook(name)),
    );
    this.publishSheetCount();
    return sheet.revision;
  }

  // ------------------------------------------------- sheets_ui, sheets_view, sheets_selection (MCP contract, 6 Oct)

  private readonly validationPanel = viewChild(ValidationPanelComponent);
  private readonly cfPanel = viewChild(CfPanelComponent);
  private readonly splitPanel = viewChild(SplitPanelComponent);
  private readonly pdfDialog = viewChild(PdfDialogComponent);
  /** The expanded header's menus, which it lends while it shows. */
  private readonly chrome = inject(EditorChrome);
  /** "Open in Modal" and "Open in Dynamic Panel" are the app's; a page without them has neither. */
  private readonly modals = inject(ModalService, { optional: true });
  private readonly dynamicPanel = inject(DynamicPanelService, { optional: true });

  /**
   * What this editor lends the UI commands (ui-commands.ts): each surface and control as its own button, menu
   * item or field has it, so a command does what a person's click does, and only that.
   */
  private readonly uiHost: UiHost = {
    grid: () => this.handle?.ui ?? null,
    context: () => this.handle?.context() ?? null,
    isOpen: (surface) => this.surfaceOpen(surface),
    open: (surface) => this.openSurface(surface),
    close: (surface) => this.closeSurface(surface),
    inspector: (surface) => this.inspectorFor(surface),
    busy: (surface) => (surface === 'dialog.pdf' && this.pdfBusy()) || (surface === 'dialog.import' && this.importBusy()),
    lastMessage: () => this.toast(),
    isDisabled: (control) => this.isDisabled(control),
    press: (control, double) => (double && control.dblclick ? this.run(control.dblclick) : this.runMain(control)),
    optionState: (option) => this.optionState(option),
    choose: (option) => this.chooseOption(option),
    focusToolbar: () => {
      // NFR-7: the first control in the row; Left and Right move on from there.
      const first = this.barRef?.nativeElement.querySelector<HTMLElement>('.tb__slot:not(.tb__slot--out) :is(button, select):not([disabled])');
      if (!first) return UI_REASONS.toolbarHidden;
      first.focus();
      return null;
    },
    shareable: () => this.boundToItem(),
    fontFamilies: () => this.fontFamilies(),
    switchItemTab: (tab) => this.switchItemTab(tab),
    openCompanion: (mode) => this.openCompanion(mode),
    openSettings: () => {
      // The item menu's "Settings" (StitchContextMenuComponent.openSettings).
      void this.router.navigate([`/settings/${Application.sheets}`]);
      return null;
    },
    openWorkbook: (id, sheet) => this.openWorkbookFor(id, sheet),
    itemMenu: () => this.chrome.itemMenu(),
    closeWorkbook: () => {
      // The header's "Close workbook" (SheetsLayoutComponent.close).
      if (!this.boundToItem()) return UI_REASONS.noItemToClose;
      // A hosted editor has no route of its own to leave: its host closes it (SBUX-1043).
      if (this.hosted()) {
        this.closeRequested.emit();
        return null;
      }
      void this.router.navigate([this.router.url.startsWith('/sheets-spike') ? '/sheets-spike' : '/sheets']);
      return null;
    },
    extraState: () => ({
      workbookId: this.sheet?.id ?? null,
      saveState: this.saveState(),
      itemTab: this.itemTab(),
      // The workbook's own view settings, as the toolbar shows them (sheets_apply changes them).
      view: { gridlines: this.format().gridlines, frozen: this.format().frozen },
      // §5.6: only the person chooses which version to keep; it is shown, never answered.
      ...(this.conflictOpen() ? { awaitingPerson: UI_REASONS.conflictOpen } : {}),
      ...(this.saveState() === 'error_blocked' ? { banner: this.blockedReason() } : {}),
      ...(this.toast() ? { toast: this.toast() } : {}),
      toolbarOverflow: this.toolbar.slice(this.shownCount()).flatMap((c) => (c ? [c.id] : [])),
    }),
    // A task: change detection runs first, so a panel just opened is there to read its fields from.
    settle: () => new Promise((resolve) => setTimeout(resolve)),
  };

  private surfaceOpen(surface: SheetsUiSurface): boolean {
    // The engine's own panels and popups (engine-panels.ts).
    if (isEnginePanel(surface)) return this.handle?.panels.isOpen(surface) ?? false;
    switch (surface) {
      case 'menu.overflow':
        return this.overflowOpen();
      case 'menu.download':
        return this.boundToItem() ? (this.chrome.menus()?.downloadOpen() ?? false) : this.menuOpen();
      case 'menu.item_actions':
        return this.chrome.menus()?.itemMenuOpen() ?? false;
      case 'select.font':
        return false;
      case 'dialog.pdf':
        return this.pdfOpen();
      case 'dialog.import':
        return this.importOpen();
      case 'dialog.shortcuts':
        return this.shortcutsOpen();
      case 'dialog.find':
        return this.handle?.ui.findOpen() ?? false;
      case 'panel.validation':
        return this.validationOpen();
      case 'panel.conditional_formatting':
        return this.cfOpen();
      case 'panel.split_text':
        return this.splitOpen();
      default:
        return this.openMenu() === surface.slice('menu.'.length);
    }
  }

  /** Each surface opened by what opens it for a person: its button, its menu item. */
  private openSurface(surface: SheetsUiSurface): string | null | Promise<string | null> {
    if (isEnginePanel(surface)) {
      // As its toolbar menu entry opens it.
      const { menu, option } = SHEETS_UI_ENGINE_PANELS[surface];
      const entry = menuOptions(menu.slice('menu.'.length)).find((o) => o.id === option);
      if (!this.handle || !entry) return UI_REASONS.loading;
      return this.handle.panels.open(surface, () => this.chooseOption(entry));
    }
    const bar = this.barRef?.nativeElement;
    switch (surface) {
      case 'menu.overflow': {
        const button = bar?.querySelector<HTMLElement>('.tb__overflowhost > button');
        if (!button) return UI_REASONS.noOverflow;
        this.toggleOverflow(button);
        return null;
      }
      case 'menu.download': {
        if (!this.boundToItem()) {
          const button = this.hostRef.nativeElement.closest('.sheets-editor')?.querySelector<HTMLElement>('.sheets-editor__share button');
          if (!button) return UI_REASONS.downloadHidden;
          this.toggleDownload(button);
          return null;
        }
        const menus = this.chrome.menus();
        if (!menus) return UI_REASONS.downloadHidden;
        menus.setDownloadOpen(true);
        return null;
      }
      case 'menu.item_actions': {
        const menus = this.chrome.menus();
        if (!menus || !this.boundToItem()) return UI_REASONS.noItemMenu;
        menus.setItemMenuOpen(true);
        return null;
      }
      case 'select.font':
        return UI_REASONS.fontOpensNothing;
      case 'dialog.pdf':
        this.onDownload('pdf');
        return null;
      case 'dialog.import':
        this.onDownload('import');
        return null;
      case 'dialog.shortcuts':
        this.onDownload('shortcuts');
        return null;
      case 'dialog.find':
        void this.run(toolbarControl('find')!.main!);
        return null;
      case 'panel.validation':
        this.toggleValidation();
        return null;
      case 'panel.conditional_formatting':
        // "Manage rules": the panel as it is.
        this.runStitch('cf-panel', undefined);
        return null;
      case 'panel.split_text':
        this.openSplit();
        return null;
      default: {
        const control = toolbarControl(surface.slice('menu.'.length));
        if (!control) return UI_REASONS.noSurface(surface);
        const at = this.toolbar.indexOf(control);
        // A control that doesn't fit the row is in "More tools", which opens first.
        if (at >= this.shownCount() && !this.overflowOpen()) {
          const more = bar?.querySelector<HTMLElement>('.tb__overflowhost > button');
          if (more) this.toggleOverflow(more);
        }
        this.toggleMenu(control, bar?.querySelector<HTMLElement>(`[data-slot="${at}"] [aria-haspopup="menu"]`) ?? undefined);
        return null;
      }
    }
  }

  /** Each surface closed as its X, Escape or a click elsewhere closes it. */
  private closeSurface(surface: SheetsUiSurface): string | null {
    if (isEnginePanel(surface)) return this.handle ? this.handle.panels.close(surface) : UI_REASONS.loading;
    switch (surface) {
      case 'menu.overflow':
        this.overflowOpen.set(false);
        return null;
      case 'menu.download':
        if (this.boundToItem()) this.chrome.menus()?.setDownloadOpen(false);
        else this.menuOpen.set(false);
        return null;
      case 'menu.item_actions':
        this.chrome.menus()?.setItemMenuOpen(false);
        return null;
      case 'select.font':
        return null;
      case 'dialog.pdf':
        // Both its buttons are disabled while the PDF is made.
        if (this.pdfBusy()) return UI_REASONS.pdfBusy;
        this.pdfOpen.set(false);
        return null;
      case 'dialog.import':
        if (this.importBusy()) return UI_REASONS.importBusy;
        this.importOpen.set(false);
        return null;
      case 'dialog.shortcuts':
        this.shortcutsOpen.set(false);
        return null;
      case 'dialog.find':
        return this.handle ? this.handle.ui.closeFind() : UI_REASONS.loading;
      case 'panel.validation':
        this.validationOpen.set(false);
        return null;
      case 'panel.conditional_formatting':
        this.cfOpen.set(false);
        return null;
      case 'panel.split_text':
        this.splitOpen.set(false);
        return null;
      default:
        if (this.openMenu() === surface.slice('menu.'.length)) this.openMenu.set(null);
        return null;
    }
  }

  private inspectorFor(surface: SheetsUiInspector): UiInspector | null {
    if (!this.surfaceOpen(surface)) return null;
    if (isEnginePanel(surface)) return this.handle?.panels.inspector(surface) ?? null;
    switch (surface) {
      case 'panel.validation':
        return this.validationPanel() ?? null;
      case 'panel.conditional_formatting':
        return this.cfPanel() ?? null;
      case 'panel.split_text':
        return this.splitPanel() ?? null;
      case 'dialog.pdf':
        return this.pdfDialog() ?? null;
    }
  }

  private optionState(option: UiOption): { checked: boolean; disabled: boolean } {
    const action = option.action;
    switch (action.kind) {
      case 'entry':
        return { checked: this.entryChecked(action.entry), disabled: this.entryDisabled(action.entry) };
      case 'colour': {
        const current = action.picker === 'text' ? this.textColour() : action.picker === 'fill' ? this.fillColour() : this.borderColour();
        return { checked: current.toUpperCase() === action.colour.toUpperCase(), disabled: false };
      }
      case 'line':
        return { checked: this.borderStyle() === action.style, disabled: false };
      case 'font':
        return { checked: this.format().fontFamily === action.family, disabled: false };
      case 'download':
        return { checked: false, disabled: false };
    }
  }

  /** An option clicked: its entry run, its swatch or line style picked, its download started. */
  private chooseOption(option: UiOption): Promise<string | null> {
    const action = option.action;
    switch (action.kind) {
      case 'entry':
        return this.run(action.entry.run);
      case 'colour':
        return this.pickColour(action.picker, action.colour);
      case 'line':
        this.borderStyle.set(action.style);
        return Promise.resolve(null);
      case 'font':
        return this.run(toolbarControl('font')!.main!, action.family);
      case 'download':
        if (this.boundToItem()) this.chrome.menus()?.setDownloadOpen(false);
        this.onDownload(action.action);
        return Promise.resolve(null);
    }
  }

  /**
   * One of the workbook's item tabs, in place: the tabs are this page's own (SBUX-1036), as the prototype's editor has
   * them, and the grid stays mounted behind the others. `sheets` (the prototype's name) and `workbook` (its earlier
   * one) are the grid's tab, the form's `sheet`.
   */
  private switchItemTab(tab: SheetsUiItemTab): string | null {
    const sheet = this.sheet;
    const tabs = this.chrome.itemTabs();
    if (!sheet || !this.boundToItem() || !tabs || tabs.id() !== sheet.id) return UI_REASONS.noItemTabs;
    return tabs.select(tab === 'sheets' || tab === 'workbook' ? 'sheet' : tab) ? null : UI_REASONS.noItemTabs;
  }

  /** The item tab showing, by the UI commands' names (`sheets` for the grid's); null without the tabs. */
  private itemTab(): string | null {
    const tabs = this.chrome.itemTabs();
    if (!this.sheet || !tabs || tabs.id() !== this.sheet.id) return null;
    const tab = tabs.current();
    return tab === 'sheet' ? 'sheets' : tab;
  }

  /** The item menu's "Open in Modal" and "Open in Dynamic Panel" (StitchContextMenuComponent), for this workbook. */
  private async openCompanion(mode: 'modal' | 'dynamic_panel'): Promise<string | null> {
    const sheet = this.sheet;
    const service = this.sheetsService;
    if (!sheet || !service || !this.boundToItem()) return UI_REASONS.noItemToOpen;
    if (mode === 'modal' ? !this.modals : !this.dynamicPanel) return UI_REASONS.noCompanion(mode);
    // "Open in Dynamic Panel", asked of the editor that IS in the Dynamic Panel: `setFormItem` would
    // push a form over this editor and destroy it mid-command (SBUX-1043). A modal is still its own
    // window, so it stays available.
    if (mode === 'dynamic_panel' && this.hosted()) return UI_REASONS.alreadyInPanel;
    const item = await firstValueFrom(service.getItem(sheet.id));
    if (mode === 'modal') this.modals!.openFullForm(item, this.importInjector);
    else this.dynamicPanel!.setFormItem(item);
    return null;
  }

  /**
   * `open_workbook`: the workbook in this editor's place (`/sheets/open?id=…`), as "Open" in its menu does. Asked of
   * the workbook already open, it only shows the sheet asked for.
   */
  private openWorkbookFor(workbookId: string, sheet?: SheetsUiSheetRef): string | null {
    if (this.sheet?.id === workbookId) {
      if (!sheet) return null;
      const outcome = this.handle?.navigate({ activateSheet: sheet });
      if (!outcome) return UI_REASONS.loading;
      return 'error' in outcome ? outcome.error : null;
    }
    // Another workbook's sheets are known by id before it loads (sheets_get_summary lists them).
    if (sheet && !('id' in sheet)) return UI_REASONS.sheetById;
    void openWorkbookInEditor(this.router, workbookId, sheet && 'id' in sheet ? sheet.id : undefined);
    return null;
  }

  /** "Import workbook…": the chosen file is becoming a workbook, and the dialog stays open, busy (gap row 72). */
  protected readonly importBusy = signal(false);
  /** The app's toasts and search, looked up when an import runs: an editor that never imports needs neither. */
  private readonly importInjector = inject(Injector);
  /** SBUX-1145: in the Sheets app's main view, a workbook this editor imports opens full-window. */
  private readonly fullWindowStart = injectFullWindowStart(Application.sheets);

  /**
   * A new workbook from an imported file, opened once it exists: the library's "Import spreadsheet" and this
   * are one flow (new-workbook.ts), so both keep the dialog open and busy until it's done, give a failure's
   * own reason, and say "Workbook imported" with what didn't transfer (gap rows 72 and 77).
   *
   * Its toast is the app's, not this editor's: the editor is replaced by the new workbook's (B-189), and a
   * toast of its own went with it. The open workbook is untouched either way, which is the point.
   */
  private async createFromImport(file: File): Promise<void> {
    this.importBusy.set(true);
    const opened = await importAndOpenWorkbook(
      {
        sheetsService: this.sheetsService!,
        router: this.router,
        toaster: this.importInjector.get(ToasterService),
        // Row 10's library reset, as from the library's own import: only inside Sheets, where both are provided.
        library: this.importLibrary(),
      },
      file,
      // SBUX-1145: a new workbook made in the Sheets app opens full-window, this import's included (Codex R01 MD-02).
      { open: openInEditorFullWindow(this.router, this.fullWindowStart) },
    );
    this.importBusy.set(false);
    if (opened) this.importOpen.set(false);
  }

  private importLibrary(): LibraryView | null {
    const search = this.importInjector.get(AdvancedSearchService, null);
    return this.sheetsState && search ? { search, folders: this.sheetsState } : null;
  }

  /** Resolves once the element has a non-zero height, or after a bounded wait. */
  private async whenLaidOut(el: HTMLElement, attempts = 20): Promise<void> {
    for (let i = 0; i < attempts; i++) {
      if (el.offsetHeight > 0) return;
      await new Promise<void>((resolve) => setTimeout(resolve, 16));
    }
    console.warn('[sheets-editor] container never gained height; mounting anyway');
  }

  /** The header's "<n> sheets" (SBUX-1036): the workbook as it is now, a sheet added or removed included. */
  private publishSheetCount(): void {
    if (!this.boundToItem()) return;
    const count = this.handle?.engine.getActiveWorkbook()?.getSheets().length ?? null;
    if (count !== this.saveStatus.sheets()) this.zone.run(() => this.saveStatus.sheets.set(count));
  }

  private onLocalMutation(): void {
    this.lastMutationAt = Date.now();
    this.publishSheetCount();
    this.zone.run(() => {
      if (this.machine) this.machine.localMutation();
      else this.persistUnbound();
    });
  }

  /**
   * The stored workbook for ?id=, or the fixture.
   *
   * An empty stored snapshot ({} -- the column's default, so every workbook created
   * from the UI) cannot be handed to the engine: it has no sheets and mounts a grid
   * with nothing in it, not even a tab. A BLANK workbook stands in, not the fixture:
   * showing invented data to someone who just pressed New Workbook is worse than
   * showing nothing, because it looks like theirs.
   */
  private async loadSnapshot(): Promise<ReturnType<typeof buildSpikeWorkbook>> {
    const id = this.requestedId();
    if (!id || !this.sheetsService) {
      // Nothing to save to, from the first frame — say so rather than opening on "Saved".
      this.zone.run(() => this.saveState.set('unbound'));
      return buildSpikeWorkbook();
    }

    try {
      const sheet = await new Promise<Sheet>((resolve, reject) =>
        this.sheetsService!.getItem(id).pipe(take(1)).subscribe({ next: resolve, error: reject }),
      );
      // Closed meanwhile: the preview must not switch back to this workbook.
      if (this.destroyed) return buildEmptyWorkbook(sheet.title);
      this.sheet = sheet;
      this.zone.run(() => {
        this.boundToItem.set(true);
        this.docTitle.set(sheet.title || SHEETS_STRINGS.Untitled);
        // Tell the section what is open. Reaching the editor through the UI sets this on the way in,
        // but a deep link (bookmark, shared URL, reload) arrives with the id in the query string and
        // nothing selected — which left the expanded header reading "Untitled" over the right data.
        // Not from a hosted editor: the fourth panel's workbook is not the main app's selection, and
        // opening one there would move the section off whatever the user was looking at.
        if (!this.hosted()) this.sheetsState?.setMainView(sheet);
      });
      // AM-48: the title is the workbook's name. The stored name can be older (a rename from the list changes
      // the title alone, and the next save brings the name in line), and downloads are named from it.
      const stored = sheet.snapshot as { sheetOrder?: unknown[] } | undefined;
      return stored?.sheetOrder?.length
        ? ({ ...stored, name: workbookName(sheet.title) } as ReturnType<typeof buildSpikeWorkbook>)
        : buildEmptyWorkbook(workbookName(sheet.title));
    } catch {
      // A workbook that will not load must not silently show someone else's data.
      this.zone.run(() => {
        this.showToast(LOAD_ERROR_TOAST);
        // The blank grid it falls back to is bound to nothing, so it opens on "Saved" unless told
        // otherwise — the toast fades and the lie stays on screen.
        this.saveState.set('unbound');
      });
      return buildEmptyWorkbook();
    }
  }

  /**
   * An edit with no item to save to: the /sheets-spike sandbox by design, or a workbook whose load
   * failed. Nothing is written anywhere, the console included (#1447 R5x: console only via DevLogService).
   */
  private persistUnbound(): void {
    // NOT 'clean'. That rendered as "Saved" over an editor writing to nothing.
    this.saveState.set('unbound');
  }

  /**
   * ALG-APPLY-REMOTE step 4 for Stitchy (AM-38): "Stitchy {summary}", with Undo and Review. The
   * summary and changed ranges are the revision's own record; when it cannot be read, the toast
   * says less and Review has nothing to outline.
   */
  private async showStitchyChange(workbookId: string, revision: number, previous: unknown): Promise<void> {
    try {
      const change = await getRevisionChange(this.http, workbookId, revision);
      if (this.destroyed) return;
      // The grid shows the revision the reload got, which can be newer than the event's: another tab's
      // save is not Stitchy's change (its toast said "Stitchy changed this workbook", and Undo threw the
      // other tab's edit away). And a slower answer for an older revision doesn't replace a newer toast.
      if (change && change.actor !== 'stitchy') {
        this.zone.run(() => this.showToast(OTHER_TAB_TOAST));
        return;
      }
      const showing = this.stitchyToast();
      if (showing && showing.revision > revision) return;
      this.zone.run(() => {
        this.stitchyToast.set({
          text: stitchyToastText(change?.summary),
          revision,
          ranges: change?.changedRanges ?? [],
          previous,
        });
        if (this.stitchyToastTimer) clearTimeout(this.stitchyToastTimer);
        this.stitchyToastTimer = setTimeout(() => this.zone.run(() => this.stitchyToast.set(null)), STITCHY_TOAST_MS);
      });
    } catch (error) {
      // The grid already shows the change; only its toast is lost.
      console.error('[sheets] AM-38: could not show Stitchy’s change', error);
    }
  }

  /**
   * The toast's Undo: the document Stitchy's change replaced goes back on the grid, and is saved
   * as the user's own revision (AC-REPLACE-1). Only while nothing has happened since: once the
   * user has edited, or another revision has arrived, undoing would throw that away.
   */
  protected undoStitchy(): void {
    const shown = this.stitchyToast();
    this.stitchyToast.set(null);
    if (!shown || !this.machine || !this.handle) return;
    // A cell being typed in counts as a change since: replacing the document under the open cell editor
    // lost the text, or put it into the restored document.
    if (this.machine.state !== 'clean' || this.machine.baseRevision !== shown.revision || this.handle.isCellEditing()) {
      this.showToast(fill('Undo is no longer available: the workbook has changed since.'));
      return;
    }
    this.zone.runOutsideAngular(() => this.handle?.replace(shown.previous));
    // `replace` is silent by design (ALG-APPLY-REMOTE step 3), so the save is started here.
    this.onLocalMutation();
  }

  /** The toast's Review: Stitchy's changed ranges outlined for 6,000 ms. */
  protected reviewStitchy(): void {
    const shown = this.stitchyToast();
    if (!shown || !this.handle) return;
    const outlined = this.zone.runOutsideAngular(() =>
      this.handle?.highlightRanges(
        shown.ranges,
        { stroke: REVIEW_STROKE, strokeWidth: REVIEW_STROKE_WIDTH },
        REVIEW_MS,
      ),
    );
    if (!outlined) this.showToast(fill('There is nothing on the grid to outline for this change.'));
  }

  /** NFR-7: Left/Right (and Home/End) move focus along the toolbar; the open download menu keeps its own keys. */
  protected toolbarKey(event: KeyboardEvent): void {
    const bar = event.currentTarget as HTMLElement;
    if ((document.activeElement as HTMLElement | null)?.closest('.tb__menu, .tb__overflow')) return;
    const controls = Array.from(bar.querySelectorAll<HTMLElement>('button, select')).filter(
      (c) =>
        !c.hasAttribute('disabled') &&
        !c.closest('app-download-menu') &&
        !c.closest('.tb__menu') &&
        !c.closest('.tb__overflow') &&
        !c.closest('.tb__slot--out'),
    );
    const target = toolbarTarget(event.key, controls.indexOf(document.activeElement as HTMLElement), controls.length);
    if (target === null) return;
    event.preventDefault();
    controls[target].focus();
  }

  protected showToast(message: string, ms = TOAST_MS, viewRevision?: number): void {
    this.toast.set(message);
    this.toastVersions.set(viewRevision ?? null);
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(
      () =>
        this.zone.run(() => {
          this.toast.set(null);
          this.toastVersions.set(null);
        }),
      ms,
    );
  }

  /** The toast's "View versions": version history at Stitchy's replaced revision, as Slides' does (SBUX-1146). */
  protected viewVersions(): void {
    const revision = this.toastVersions();
    const id = this.sheet?.id;
    if (!id || revision === null) return;
    void this.dialogInjector.get(SheetVersionHistoryDialog).open(id, this.dialogInjector, revision, this.workbookTenant() ?? undefined);
  }
  private readonly dialogInjector = inject(Injector);

  // ------------------------------------------------- the toolbar (§11.6.3)

  protected isActive(state: ToolbarState | undefined): boolean {
    const f = this.format();
    switch (state) {
      case undefined:
        return false;
      case 'validation-open':
        return this.validationOpen();
      case 'cf-open':
        return this.cfOpen();
      case 'find-open':
        return f.findOpen;
      default:
        return f[state];
    }
  }

  protected isDisabled(control: ToolbarControl): boolean {
    if (control.disabledWhen === 'no-undo') return !this.format().canUndo;
    if (control.disabledWhen === 'no-redo') return !this.format().canRedo;
    return false;
  }

  /**
   * A control's tooltip (and so its label, NFR-7) with its shortcut written for the platform: the pack's "Undo
   * (⌘Z)" on a Mac, "Undo (Ctrl+Z)" on Windows and Linux (Oleg, 2026-10-06). The bindings are the same.
   */
  protected tip(control: ToolbarControl): string {
    return shortcutText(control.tooltip);
  }

  /** A menu entry's shortcut hint (Insert's "⌘K", "⌥⌘M"), likewise; a number format's example is unchanged. */
  protected keys(text: string): string {
    return shortcutText(text);
  }

  /** #5: the families, with the active cell's own when it is not one of them. */
  protected fontFamilies(): readonly string[] {
    const current = this.format().fontFamily;
    return FONT_FAMILIES.includes(current) ? FONT_FAMILIES : [current, ...FONT_FAMILIES];
  }

  /** #18: the matching entry's name, or "Custom". */
  protected numberFormatName(): string {
    const pattern = this.format().numberFormat || 'General';
    return NUMBER_FORMATS.find((f) => f.pattern === pattern)?.name ?? SHEETS_STRINGS.Custom;
  }

  /** #15–17: the icon shows the current value. */
  protected iconFor(control: ToolbarControl): string | undefined {
    const f = this.format();
    if (control.id === 'h-align') return `align-${f.hAlign ?? 'left'}`;
    if (control.id === 'v-align') return `valign-${f.vAlign ?? 'bottom'}`;
    if (control.id === 'wrap-rotate') return `wrap-${f.wrap}`;
    return control.icon;
  }

  public ngAfterViewInit(): void {
    const bar = this.barRef?.nativeElement;
    if (!bar || typeof ResizeObserver === 'undefined') return;
    this.barObserver = new ResizeObserver(() => this.zone.run(() => this.fitToolbar()));
    this.barObserver.observe(bar);
    // The first fit does not wait for the observer: it reports only while the page renders, and a
    // tab opened in the background does not (found live).
    setTimeout(() => this.zone.run(() => this.fitToolbar()));
  }

  /** Puts in the row as many items as fit, in order; the rest go to "More tools". */
  private fitToolbar(): void {
    const bar = this.barRef?.nativeElement;
    if (!bar) return;
    const gap = parseFloat(getComputedStyle(bar).columnGap) || 0;
    for (const slot of Array.from(bar.querySelectorAll<HTMLElement>('[data-slot]'))) {
      this.slotWidths[Number(slot.dataset['slot'])] = slot.getBoundingClientRect().width + gap;
    }
    const style = getComputedStyle(bar);
    const inner = bar.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    // Anything else in the row, and room for "More tools".
    const tail = Array.from(bar.children)
      .filter(
        (el) =>
          !el.hasAttribute('data-slot') &&
          !el.classList.contains('tb__overflowhost') &&
          getComputedStyle(el).position !== 'absolute',
      )
      .reduce((sum, el) => sum + (el as HTMLElement).getBoundingClientRect().width + gap, 0);
    const moreButton = 44;
    const next = toolbarFit(this.toolbar, this.slotWidths, inner - tail, moreButton);
    if (next !== this.shownCount()) {
      this.shownCount.set(next);
      if (next === this.toolbar.length) this.overflowOpen.set(false);
    }
  }

  protected toggleOverflow(button: HTMLElement): void {
    if (this.overflowOpen()) {
      this.overflowOpen.set(false);
      return;
    }
    const box = button.getBoundingClientRect();
    this.overflowAt.set({ right: Math.max(8, window.innerWidth - box.right), top: box.bottom + 4 });
    this.overflowOpen.set(true);
  }

  /**
   * The Download and share menu of an editor bound to no workbook (the spike page, a failed load), in its own
   * title row; bound, the menu is the expanded header's. Placed fixed under its button, as the menus below.
   */
  protected readonly downloadAt = signal({ right: 8, top: 0 });

  protected toggleDownload(button: HTMLElement): void {
    if (this.menuOpen()) {
      this.menuOpen.set(false);
      return;
    }
    const box = button.getBoundingClientRect();
    this.downloadAt.set({ right: Math.max(8, window.innerWidth - box.right), top: box.bottom + 4 });
    this.menuOpen.set(true);
  }

  /** The toolbar scrolls sideways, which would clip a menu inside it, so a menu is placed fixed. */
  protected readonly menuAt = signal({ left: 0, top: 0 });

  protected toggleMenu(control: ToolbarControl, anchor?: HTMLElement): void {
    if (this.openMenu() === control.id) {
      this.openMenu.set(null);
      return;
    }
    const box = (anchor?.closest('.tb__menuhost') ?? anchor)?.getBoundingClientRect();
    if (box) this.menuAt.set({ left: Math.min(box.left, window.innerWidth - 280), top: box.bottom + 4 });
    this.openMenu.set(control.id);
  }

  /**
   * A press anywhere but the open menu and its button closes it. Heard in the capture phase: the grid stops
   * its own presses from bubbling, so a click in the grid left every menu open (live pass, 2026-10-03). The
   * Download and share menu closes this way too; it closed only from its own button before.
   */
  private readonly closeMenuOutside = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (this.openMenu() && !target?.closest('.tb__menuhost')) this.openMenu.set(null);
    if (this.overflowOpen() && !target?.closest('.tb__overflowhost')) this.overflowOpen.set(false);
    if (this.menuOpen() && !target?.closest('.tb__menuhost')) this.menuOpen.set(false);
  };

  /** In a menu, Up and Down move between its items; Escape closes it, focus going back to its button. */
  protected menuKey(event: KeyboardEvent, button: HTMLElement): void {
    const menu = (event.currentTarget as HTMLElement).querySelector('.tb__menu');
    if (event.key === 'Escape' && menu) {
      event.stopPropagation();
      this.openMenu.set(null);
      button.focus();
      return;
    }
    if (!menu || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
    const items = Array.from(menu.querySelectorAll<HTMLElement>('button, select'));
    const at = items.indexOf(document.activeElement as HTMLElement);
    event.preventDefault();
    event.stopPropagation();
    const next = event.key === 'ArrowDown' ? (at + 1) % items.length : (at <= 0 ? items.length : at) - 1;
    items[next]?.focus();
  }

  protected entryRun(entry: MenuEntry): ToolbarRun | null {
    return 'run' in entry ? entry.run : null;
  }

  /** AM-87: "Clear all conditions" needs a condition, "Re-apply filter" a filter. */
  protected entryDisabled(entry: MenuEntry): boolean {
    const when = 'disabledWhen' in entry ? entry.disabledWhen : undefined;
    if (when === 'no-filter') return !this.format().filter;
    if (when === 'no-filter-conditions') return !this.format().filterConditions;
    return false;
  }

  protected entryChecked(entry: MenuEntry): boolean {
    return 'checkedWhen' in entry && this.isActive(entry.checkedWhen);
  }

  /**
   * A button's main part; #11 and #12 apply the last-used colour. Resolves to why it didn't run, or null, for
   * `sheets_ui` (a click ignores it).
   */
  protected runMain(control: ToolbarControl): Promise<string | null> {
    if (!control.main) {
      this.toggleMenu(control);
      return Promise.resolve(null);
    }
    if (control.id === 'text-colour') return this.run(control.main, this.textColour());
    if (control.id === 'fill-colour') return this.run(control.main, this.fillColour());
    return this.run(control.main);
  }

  /** A colour from §11.7's picker. */
  protected pickColour(picker: 'text' | 'fill' | 'border', colour: string): Promise<string | null> {
    if (picker === 'border') {
      this.borderColour.set(colour);
      return Promise.resolve(null);
    }
    (picker === 'text' ? this.textColour : this.fillColour).set(colour);
    return this.run(
      { command: picker === 'text' ? 'sheet.command.set-range-text-color' : 'sheet.command.set-background-color' },
      colour,
    );
  }

  /** Runs a control; resolves to the reason it refused (what its toast says), or null. */
  protected run(run: ToolbarRun, value: ToolbarValue | undefined = run.value): Promise<string | null> {
    this.openMenu.set(null);
    this.overflowOpen.set(false);
    if ('stitch' in run) {
      this.runStitch(run.stitch, value);
      return Promise.resolve(null);
    }
    // B-177: the split needs its separator first, which the panel asks for.
    if (run.command === 'sheet.command.split-text-to-columns' && value === undefined) {
      this.openSplit();
      return Promise.resolve(null);
    }
    const handle = this.handle;
    if (!handle) return Promise.resolve(UI_REASONS.loading);
    let ran: Promise<boolean>;
    try {
      ran = handle.runToolbar(run.command, value, { style: this.borderStyle(), color: this.borderColour() });
    } catch (err) {
      console.warn('[sheets-editor] toolbar command refused', run.command, err);
      this.showToast(fill('Select a cell first'));
      return Promise.resolve(fill('Select a cell first'));
    }
    return ran.then(
      (done) => (done ? null : UI_REASONS.notRun),
      (err: unknown) => {
        console.warn('[sheets-editor] toolbar command failed', run.command, err);
        const message = err instanceof Error ? err.message : SHEETS_STRINGS['That did not work'];
        this.zone.run(() => this.showToast(message));
        return message;
      },
    );
  }

  private runStitch(action: Extract<ToolbarRun, { stitch: string }>['stitch'], value: ToolbarValue | undefined): void {
    switch (action) {
      case 'undo':
        void this.handle?.engine.undo();
        return;
      case 'redo':
        void this.handle?.engine.redo();
        return;
      case 'validation-panel':
        this.toggleValidation();
        return;
      case 'cf-panel':
        // A rule type opens the panel on it; "Manage rules" opens it as it is.
        this.cfPreset.set(value === undefined ? null : String(value));
        this.validationOpen.set(false);
        if (!this.cfOpen()) this.prepareCf();
        this.cfOpen.set(value === undefined ? !this.cfOpen() : true);
        return;
      case 'checkbox-rule':
        try {
          this.handle?.insertCheckbox();
        } catch (err) {
          console.warn('[sheets-editor] checkbox failed', err);
          this.showToast(fill('Select a cell first'));
        }
        return;
      case 'fullscreen':
        void this.toggleFullscreen();
        return;
    }
  }

  /**
   * The prototype's "Toggle fullscreen": the whole page in and out of fullscreen. Where the browser refuses
   * (no Fullscreen API, a policy, no user gesture), its toast. Escape is the browser's own, left alone.
   */
  private async toggleFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      this.zone.run(() => this.showToast(fill('Use your browser’s fullscreen shortcut.')));
    }
  }

  // ------------------------------------------------- download and share

  protected onDownload(a: DownloadAction): void {
    this.menuOpen.set(false);
    switch (a) {
      case 'csv':
        void this.exportDelimited(',', 'csv');
        break;
      case 'tsv':
        void this.exportDelimited('\t', 'tsv');
        break;
      case 'xlsx':
      case 'xls':
      case 'ods':
        void this.exportSpreadsheet(a);
        break;
      case 'pdf':
        this.refreshPdfSheets();
        this.pdfOpen.set(true);
        break;
      case 'import':
        this.importOpen.set(true);
        break;
      case 'import-csv':
        this.pickCsvSheet();
        break;
      case 'shortcuts':
        this.shortcutsOpen.set(true);
        break;
      case 'share':
        this.shareViaLink();
        break;
      default:
        assertExhaustive(a);
    }
  }

  /**
   * "Share via link" (editor review row 47): the workbook's read-only share link (AM-93, AM-96), made and
   * copied as the library's Share menu does it: the link it already has, or a new one from the server, kept
   * on the item so the library's menu shows it shared. It copied the editor's own address before, which only
   * the owner can open.
   */
  private shareViaLink(): void {
    const sheet = this.sheet;
    const service = this.sheetsService;
    if (!sheet || !service) return;
    const link$ = sheet.shareUid
      ? of(sheet.sharedLink)
      : service.shareViaLink(sheet).pipe(
          tap((link) => {
            if (!link) return;
            sheet.shareUid = link.substring(link.lastIndexOf('/') + 1);
            sheet.sharedLink = link;
          }),
        );
    link$.pipe(take(1)).subscribe({
      next: (link) => {
        if (link) void this.copyLink(link);
      },
      error: (error: unknown) => this.zone.run(() => this.showToast(requestFailedToast(error))),
    });
  }

  /** The link on the clipboard; where nothing could copy it, the link itself, long enough to copy by hand. */
  private async copyLink(link: string): Promise<void> {
    try {
      await copyText(link);
      this.zone.run(() => this.showToast(fill('Link copied')));
    } catch {
      this.zone.run(() => this.showToast(link, LINK_TOAST_MS));
    }
  }

  /** §11.6.1's "Import CSV as a new sheet…": a file picker, then `sheet.import_csv` in the editor. */
  private pickCsvSheet(): void {
    // Not a file picker that leads nowhere: the sheet goes into the engine.
    if (!this.handle) return this.notReady();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.tsv';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) void this.importCsvSheet(file);
    };
    input.click();
  }

  private async importCsvSheet(file: File): Promise<void> {
    // Wrapped whole, like the exports: called as `void`, so nothing else would catch a failure.
    try {
      const handle = this.handle;
      if (!handle) return this.notReady();
      const result = planCsvSheet(file.name, new Uint8Array(await file.arrayBuffer()), handle.sheetNames());
      if ('error' in result) return this.zone.run(() => this.showToast(result.error));
      this.zone.runOutsideAngular(() => handle.insertCsvSheet(result.plan));
      const { name, cells } = result.plan;
      this.zone.run(() =>
        this.showToast(
          cells === 1
            ? fill('Added sheet {name} (1 cell)', { name })
            : fill('Added sheet {name} ({n} cells)', { name, n: cells.toLocaleString('en-US') }),
        ),
      );
    } catch (err) {
      console.error('[sheets-editor] CSV import failed', err);
      this.zone.run(() => this.showToast(fill("Couldn't import {file name}", { 'file name': file.name })));
    }
  }

  private async exportSpreadsheet(format: 'xlsx' | 'xls' | 'ods'): Promise<void> {
    const wb = this.handle?.engine.getActiveWorkbook();
    if (!wb) return this.notReady();
    this.showToast(preparingToast(format.toUpperCase()));
    try {
      // AM-37/AM-41: recalculate before reading a single value, for every format.
      await recalculateBeforeExport(this.handle?.engine as never);
      const out = await this.topProgress.track(
        import('@sharedlib/sheets-editor/workbook-files').then(({ exportWorkbook }) => exportWorkbook(wb as never, format)),
      );
      if (!out) return this.showToast(fill('Nothing to export'));
      this.saveBlob(out.blob, out.filename);
      this.showToast(fill('Exported {file name}', { 'file name': out.filename }));
    } catch (err) {
      // A sheet name Excel will not take is the user's to fix, and the message already says how.
      // Everything else is ours, and "see the console" is the honest thing to say about it.
      const { SheetNameError } = await import('@sharedlib/sheets-editor/workbook-files');
      const forUser = err instanceof CalcTimeoutError || err instanceof SheetNameError;
      if (!forUser) console.error('[sheets-editor] export failed', err);
      this.showToast(exportFailedToast(err, forUser));
    }
  }

  /**
   * Rows 64 and 68: the PDF of what the dialog chose. The dialog stays open, its buttons disabled, while the
   * PDF is made, and closes when the file is; on a failure it stays open, with the toast saying why.
   */
  protected async runPdf(options: PdfOptions): Promise<void> {
    if (this.pdfBusy()) return;
    const wb = this.handle?.engine.getActiveWorkbook();
    if (!wb) return this.notReady();
    this.showToast(preparingToast('PDF'));
    this.pdfBusy.set(true);
    try {
      await recalculateBeforeExport(this.handle?.engine as never);
      const out = await this.topProgress.track(import('@sharedlib/sheets-editor/workbook-pdf').then(({ exportPdf }) => exportPdf(wb as never, options)));
      if (!out) return this.showToast(fill('Nothing to export'));
      this.saveBlob(out.blob, out.filename);
      this.pdfOpen.set(false);
      this.showToast(
        out.pages === 1
          ? fill('Exported {file name} — 1 page', { 'file name': out.filename })
          : fill('Exported {file name} — {n} pages', {
              'file name': out.filename,
              n: out.pages.toLocaleString('en-US'),
            }),
      );
    } catch (err) {
      // file-formats.md: a character the bundled font lacks, or a sheet over 100,000 cells, is the
      // user's to act on, and the message says how; so is a font that did not load.
      const { PdfExportError } = await import('@sharedlib/sheets-editor/workbook-pdf');
      const { PdfFontError } = await import('@sharedlib/sheets-editor/pdf-fonts');
      const forUser = err instanceof CalcTimeoutError || err instanceof PdfExportError || err instanceof PdfFontError;
      if (!forUser) console.error('[sheets-editor] pdf export failed', err);
      this.showToast(exportFailedToast(err, forUser));
    } finally {
      this.pdfBusy.set(false);
    }
  }

  /**
   * A download or import asked for before the engine is ready (editor review row 53): the prototype's words,
   * not "No workbook is open", which read as if nothing were open. If the engine failed to load, that.
   */
  private notReady(): void {
    this.showToast(
      this.mountFailed()
        ? fill('The spreadsheet editor failed to load — reload the page')
        : fill('The workbook is still opening. Please try again in a moment.'),
    );
  }

  private async exportDelimited(sep: string, ext: string): Promise<void> {
    if (!this.handle?.engine.getActiveWorkbook()) return this.notReady();
    // The whole body is wrapped, like `exportSpreadsheet` and `runPdf`. `onDownload` calls this as
    // `void this.exportDelimited(…)`, so there is nobody to catch anything: a throw becomes an
    // unhandled rejection and the user, having clicked CSV, sees NOTHING happen -- no file, no
    // toast, no clue. It previously rethrew any non-timeout error, and covered only the
    // recalculation, although reading the values and building the file can fail just as easily.
    this.showToast(preparingToast(ext.toUpperCase()));
    try {
      // CSV/TSV carry calculated values and no formulas, so a stale number here is permanent —
      // this is the format AM-37 matters most for, not the least.
      await recalculateBeforeExport(this.handle?.engine as never);

      const sheet = this.handle?.engine.getActiveWorkbook()?.getActiveSheet() as unknown as
        | (RowsSheet & { getSheetName(): string })
        | undefined;
      const rows = sheet ? delimitedRows(sheet) : [];
      if (!rows.length) return this.showToast(fill('Nothing to export — the sheet is empty'));
      // Display values (`getValues()`) on purpose here, unlike the xlsx export: file-formats.md gives
      // CSV/TSV as "Calculated values", so the DISPLAY string is the right thing to write. From A1,
      // as ALG-CSV-OUT says (B-187, `delimitedRows`).
      // The one writer, shared with Stitchy's export (AM-59): CRLF between records, as §14.1 says.
      // §14.1: `<title> - <sheet name>.csv`, as Stitchy's export of the sheet is named (rows 57–58).
      this.saveBlob(
        new Blob([delimitedBytes(rows, sep) as unknown as ArrayBuffer], { type: 'text/plain' }),
        downloadName(this.handle?.engine.getActiveWorkbook()?.getName(), ext, sheet?.getSheetName() ?? ''),
      );
      this.showToast(fill('Exported {n} rows to .{ext}', { n: rows.length.toLocaleString('en-US'), ext }));
    } catch (err) {
      const forUser = err instanceof CalcTimeoutError;
      if (!forUser) console.error('[sheets-editor] delimited export failed', err);
      this.showToast(exportFailedToast(err, forUser));
    }
  }

  /** §8.4's upload, telling the user when it fails (S-20). The drawing plugin shows its own state too. */
  private uploadWithToast(upload: (file: File) => Promise<{ url: string }>): (file: File) => Promise<{ url: string }> {
    return async (file) => {
      try {
        return await upload(file);
      } catch (error) {
        this.zone.run(() => this.showToast(uploadFailedToast(file.name, error)));
        throw error;
      }
    };
  }

  /** Row 56: as the prototype starts a download (`downloadBlob`); its revoke timer needs no change detection. */
  private saveBlob(blob: Blob, name: string): void {
    this.zone.runOutsideAngular(() => downloadBlob(blob, name));
  }

  /**
   * Opens a real spreadsheet in the real app. This is the integration question the
   * spike exists to answer -- exporting an xlsx proves far less than reading one
   * a third party produced.
   *
   * Bound to an item, the import CREATES A NEW WORKBOOK and opens it, which is
   * what the dialog promises in as many words: "Open a spreadsheet as a new
   * workbook. Your existing workbooks stay in place." The spike replaced the open
   * workbook in place, which was harmless while nothing persisted and would now
   * silently overwrite whichever workbook happened to be on screen.
   *
   * Unbound (the /sheets-spike route) there is no item to create, so it still
   * replaces in place through ALG-SHEETS-REPLACE -- one undoable revision.
   */
  protected async openFile(file: File): Promise<void> {
    if (this.importBusy()) return;
    if (this.boundToItem() && this.sheetsService) {
      await this.createFromImport(file);
      return;
    }
    try {
      let snapshot: unknown;
      // Unreachable from the dialog since AM-41: `checkFile` no longer accepts `.json` and the
      // picker no longer offers it. Kept because the branch is the shape a Stitchy-side
      // `sheets_import` with `format: "json"` will need, and deleting it would lose that.
      if (/\.json$/i.test(file.name)) {
        snapshot = JSON.parse(await file.text());
      } else {
        snapshot = await this.topProgress.track(
          import('@sharedlib/sheets-editor/workbook-files').then(({ importSpreadsheet }) => importSpreadsheet(file, 'spike-workbook', SNAPSHOT_STYLES)),
        );
      }
      this.importOpen.set(false);
      const sheets = (snapshot as { sheetOrder?: unknown[] }).sheetOrder?.length ?? 1;

      this.handle?.replace(snapshot);
      this.docTitle.set((snapshot as { name?: string }).name ?? file.name);
      // onLocalMutation, not `saveState.set('dirty')`: the flag marks the editor
      // dirty but arms no timer, so the import would sit unsaved for ever while
      // the UI claimed there were unsaved changes.
      this.onLocalMutation();
      this.showToast(
        sheets === 1
          ? fill('Opened {file name} — 1 sheet', { 'file name': file.name })
          : fill('Opened {file name} — {n} sheets', { 'file name': file.name, n: sheets.toLocaleString('en-US') }),
      );
    } catch (err) {
      console.error('[sheets-editor] open failed', err);
      this.showToast(err instanceof Error ? err.message : SHEETS_STRINGS['That file could not be opened']);
    }
  }
}
